import {
    ArrowLeftIcon,
    CheckCircleIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    CloudArrowUpIcon,
    DocumentTextIcon,
    EnvelopeIcon,
    PhoneIcon,
    SparklesIcon,
} from '@heroicons/react/24/outline'
import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import {
    DOC_KIND_LABEL,
    MockDocument,
    MOCK_DOCUMENTS,
    MOCK_PROVIDERS,
    PROVIDER_TYPE_LABEL,
} from './mockData'

export default function ProviderDetail() {
    const { id } = useParams()
    const provider = MOCK_PROVIDERS.find((p) => p.id === id)
    const documents = MOCK_DOCUMENTS.filter((d) => d.providerId === id)

    if (!provider) {
        return (
            <div className='max-w-3xl mx-auto px-4 py-12 text-center'>
                <p className='text-neutral-600'>Provider not found.</p>
                <Link
                    to='/admin/family-care-team-preview/team'
                    className='text-brand-600 hover:underline'
                >
                    Back to care team
                </Link>
            </div>
        )
    }

    return (
        <div className='max-w-5xl mx-auto px-4 sm:px-6 py-8'>
            <Link
                to='/admin/family-care-team-preview/team'
                className='inline-flex items-center gap-1.5 text-sm text-neutral-600 hover:text-neutral-900 mb-4'
            >
                <ArrowLeftIcon className='w-4 h-4' />
                Back to care team
            </Link>

            {/* Header */}
            <div className='flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6'>
                <div>
                    <p className='text-xs uppercase tracking-wider text-brand-700 font-semibold'>
                        {PROVIDER_TYPE_LABEL[provider.type]}
                    </p>
                    <h1 className='text-2xl sm:text-3xl font-bold text-neutral-900'>{provider.clinicianName}</h1>
                    <p className='text-sm text-neutral-600 mt-1'>{provider.organization}</p>
                </div>
                <Link
                    to='/admin/family-care-team-preview/upload'
                    className='inline-flex items-center gap-2 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium rounded-lg'
                >
                    <CloudArrowUpIcon className='w-4 h-4' />
                    Upload a document
                </Link>
            </div>

            {/* Contact info */}
            <div className='grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6'>
                <div className='bg-white border border-neutral-200 rounded-xl p-3 flex items-center gap-3'>
                    <EnvelopeIcon className='w-4 h-4 text-neutral-400' />
                    <span className='text-sm text-neutral-700'>{provider.email}</span>
                </div>
                {provider.phone && (
                    <div className='bg-white border border-neutral-200 rounded-xl p-3 flex items-center gap-3'>
                        <PhoneIcon className='w-4 h-4 text-neutral-400' />
                        <span className='text-sm text-neutral-700'>{provider.phone}</span>
                    </div>
                )}
            </div>

            {/* Documents */}
            <h2 className='text-lg font-semibold text-neutral-900 mb-3'>
                Documents ({documents.length})
            </h2>
            {documents.length === 0 ? (
                <div className='bg-white border-2 border-dashed border-neutral-300 rounded-2xl p-8 text-center'>
                    <DocumentTextIcon className='w-10 h-10 text-neutral-400 mx-auto mb-2' />
                    <p className='text-sm font-medium text-neutral-900'>No documents yet</p>
                    <p className='text-xs text-neutral-600 mt-1'>
                        Upload an evaluation, care plan, or progress note to start synthesizing.
                    </p>
                </div>
            ) : (
                <ul className='space-y-3'>
                    {documents.map((doc) => (
                        <DocumentRow key={doc.id} doc={doc} />
                    ))}
                </ul>
            )}
        </div>
    )
}

function DocumentRow({ doc }: { doc: MockDocument }) {
    const [open, setOpen] = useState(false)

    return (
        <li className='bg-white border border-neutral-200 rounded-xl overflow-hidden'>
            <div className='p-4'>
                <div className='flex items-start gap-3'>
                    <div className='w-10 h-10 rounded-lg bg-brand-50 flex items-center justify-center flex-shrink-0'>
                        <DocumentTextIcon className='w-5 h-5 text-brand-600' />
                    </div>
                    <div className='flex-1 min-w-0'>
                        <div className='flex flex-wrap items-center gap-2 mb-1'>
                            <span className='text-sm font-semibold text-neutral-900'>{doc.title}</span>
                            <span className='text-[10px] uppercase tracking-wider text-neutral-500 bg-neutral-100 px-1.5 py-0.5 rounded'>
                                {DOC_KIND_LABEL[doc.kind]}
                            </span>
                            <span
                                className={
                                    'inline-flex items-center gap-1 text-[10px] uppercase tracking-wider ' +
                                    'text-green-800 bg-green-100 px-1.5 py-0.5 rounded'
                                }
                            >
                                <CheckCircleIcon className='w-3 h-3' />
                                Analyzed
                            </span>
                        </div>
                        <div className='text-xs text-neutral-500'>
                            {doc.fileName} · {doc.pageCount} pages · uploaded {doc.uploadedAt}
                        </div>
                        <p className='text-sm text-neutral-700 mt-2'>{doc.summary}</p>
                    </div>
                </div>

                <button
                    onClick={() => setOpen((v) => !v)}
                    className='mt-3 inline-flex items-center gap-1 text-xs font-medium text-brand-700 hover:text-brand-800'
                >
                    {open ? (
                        <>
                            <ChevronUpIcon className='w-3.5 h-3.5' />
                            Hide extracted findings
                        </>
                    ) : (
                        <>
                            <ChevronDownIcon className='w-3.5 h-3.5' />
                            Show extracted findings ({doc.findings.length})
                        </>
                    )}
                </button>
            </div>

            {open && (
                <div className='border-t border-neutral-100 bg-neutral-50 p-4'>
                    <div className='grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3'>
                        {doc.findings.map((f) => (
                            <div
                                key={f.label}
                                className='bg-white border border-neutral-200 rounded-lg p-3'
                            >
                                <div className='flex items-center justify-between gap-2 mb-1'>
                                    <span className='text-[10px] uppercase tracking-wider text-neutral-500'>
                                        {f.label}
                                    </span>
                                    {f.impactsRoadmap && (
                                        <span
                                            className={
                                                'inline-flex items-center gap-0.5 text-[10px] text-brand-700 ' +
                                                'bg-brand-50 px-1.5 py-0.5 rounded'
                                            }
                                        >
                                            <SparklesIcon className='w-2.5 h-2.5' />
                                            Roadmap
                                        </span>
                                    )}
                                </div>
                                <p className='text-sm text-neutral-900'>{f.value}</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </li>
    )
}
